import { useMutation, useQueryClient } from '@tanstack/react-query';
import { CostCodeDto, CostCodeRequestDto } from 'src/api/FiretrolApiClient';
import { wbsApiClient } from 'src/api/FiretrolApiClientInstances';
import { queryKeys } from 'src/api/queries/queryKeys';
import { invalidateFor } from 'src/api/queries/invalidationMap';
import { optimisticList, rollbackList } from 'src/api/queries/optimistic';

export function useCreateCostCode() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (request: CostCodeRequestDto) =>
      wbsApiClient.createCostCode(request),
    meta: {
      successMessage: 'Cost Code successfully created!',
      suppressErrorToast: true,
    },
    onSettled: () => invalidateFor('costCode.create', qc),
  });
}

export function useUpdateCostCode() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      request,
    }: {
      id: string;
      request: CostCodeRequestDto;
    }) => wbsApiClient.updateCostCode(id, request),
    meta: {
      successMessage: 'Cost Code successfully updated!',
      suppressErrorToast: true,
    },
    onSettled: () => invalidateFor('costCode.update', qc),
  });
}

export function useDeleteCostCode() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (costCode: CostCodeDto) =>
      wbsApiClient.deleteCostCode(costCode.id).then(() => costCode),
    meta: { successMessage: 'Cost Code Deleted!' },
    onMutate: (costCode: CostCodeDto) =>
      optimisticList(qc, {
        queryKey: queryKeys.costCodes.list(),
        op: 'remove',
        entity: costCode,
        matchBy: (a, b) => a.id === b.id,
      }),
    onError: (_err, _costCode, ctx) => rollbackList(qc, ctx),
    onSettled: () => invalidateFor('costCode.delete', qc),
  });
}
